import { useQuery } from '@apollo/client';
import { getListTableColumns } from '@openx/components-segment/lib/LalSegmentsList/tableColumns.tsx';
import type { UseLalSegmentTableFetch } from '@openx/components-segment/lib/utils/context/SegmentHookTypes.ts';
import type { FiltersConfig } from '@openx/types/chipFilters.ts';
import { expiringSoonStatus, type ListLalSegment } from '@openx/types/segments.ts';
import type { RowActionProps } from '@openx/types/tableHelpers.ts';
import { firstLetterUppercase } from '@openx/utils/lib/firstLetterUppercase.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { GetProvidersListDocument } from '../../../graphql/query/providers/GetProvidersList.generated.tsx';
import { GetDistinctLalSegmentStatusesDocument } from '../../../graphql/query/segments/lal/GetDistinctLalSegmentStatuses.generated.tsx';
import { GetLalSegmentsAccountsDocument } from '../../../graphql/query/segments/lal/GetLalSegmentsAccounts.generated.tsx';
import { useLalSegmentRowActions } from '../../../modules/segments/lal/hooks/useLalSegmentRowActions.tsx';
import { isInternalUser } from '../../../permissions/validators.ts';

export const useLalSegmentTableFetch: UseLalSegmentTableFetch = () => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { data: { account: accounts = [] } = {}, loading: accountsLoading } = useQuery(GetLalSegmentsAccountsDocument);
  const { data: { provider: providers = [] } = {}, loading: providersLoading } = useQuery(GetProvidersListDocument);
  const { data: { lalSegment_statuses: segmentStatuses = [] } = {}, loading: statusesLoading } = useQuery(
    GetDistinctLalSegmentStatusesDocument
  );

  const rowActions: RowActionProps<ListLalSegment>[] = useLalSegmentRowActions();

  const statuses = useMemo(
    () =>
      segmentStatuses.reduce(
        (acc, { status }) => {
          if (status) {
            acc.push({ displayName: firstLetterUppercase(status.replaceAll('_', ' ')), status });
          }

          return acc;
        },
        [{ displayName: 'Expiring soon', status: expiringSoonStatus }]
      ),
    [segmentStatuses]
  );

  const columns = useMemo(() => {
    const tableColumns = getListTableColumns(t);

    if (!isInternalUser(session)) {
      return tableColumns.filter(({ key }) => key !== 'account');
    }

    return tableColumns;
  }, [t, session]);

  const filtersConfig: FiltersConfig = useMemo((): FiltersConfig => {
    return {
      account: {
        loading: accountsLoading,
        multiple: true,
        name: t('Organization'),
        options: accounts.map(({ name }) => ({ displayName: name, value: name })),
      },
      provider: {
        loading: providersLoading,
        multiple: true,
        name: t('Provider'),
        options: providers.map(({ name }) => ({ displayName: name ?? undefined, value: name })),
      },
      status: {
        loading: statusesLoading,
        multiple: true,
        name: t('Status'),
        options: statuses.map(item => ({ displayName: item.displayName, value: item.status })),
      },
      myDeals: {
        name: t('Show my Segments'),
        options: [{ value: session?.id }],
      },
    };
  }, [t, accounts, providers, statuses, accountsLoading, providersLoading, statusesLoading, session]);

  return {
    columns,
    filtersConfig,
    rowActions,
  };
};
